import React, { useState } from 'react';
import { Grid, MenuItem, TextField, Typography, Box, Button, Divider } from '@mui/material';
import { InventoryField, InventoryItem, isSingleItem, SkuItem } from 'common/modules/inventory';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'services/localization';
import * as UserSelectors from 'selectors/UserSelectors';
import * as InventorySelectors from 'selectors/InventorySelectors';
import { useSelector } from 'react-redux';
import SpecificationsList from '../components/SpecificationsList';
import { newFirestoreId } from 'common/utils/newRentalUtils';
import InventoryIdentifierInput from '../components/InventoryIdentifierInput';
import * as ShopSelectors from 'selectors/ShopSelectors';
import FixedFieldValuesList from '../components/FixedFieldValuesList';
import NewSpecificationDialog from '../components/NewSpecificationDialog';
import { FixedFieldValueOptions } from 'common/modules/inventory/constants';
import { getTypedKeys } from 'common/utils/objects';
import { SingleItemFormValues } from './types';
import { skuItemsToOptions } from '../utils';

interface Props {
	item: InventoryItem;
	skuItems: SkuItem[];
	inventoryFields: InventoryField[];
	control: Control<SingleItemFormValues>;
	addInventoryField: (field: InventoryField) => void;
}

export const NO_SKU_ID = 'NO_SKU_SELECTED';

const BasicInfoSection = (props: Props) => {
	const { t } = useTranslation();
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const userLocations = useSelector(UserSelectors.userLocations);
	const useDinVariation = useSelector(ShopSelectors.useDinVariation);
	const shopDinCollectionMethod = useSelector(ShopSelectors.shopDinCollectionMethod);
	const existingInventoryIds = useSelector(InventorySelectors.inventoryItemsIds);

	const [isNewSpecificationDialogOpen, setNewSpecificationDialogOpen] = useState<boolean>(false);

	const visibleFixedValueOptions = getTypedKeys(FixedFieldValueOptions).filter(
		(option) =>
			(option === 'soleLength' && !!shopDinCollectionMethod) ||
			(option === 'dinVariation' && !!useDinVariation),
	);

	const validSkuItems = isSingleItem(props.item)
		? props.skuItems.filter((sku) => sku.itemType === 'single')
		: props.skuItems.filter((sku) => sku.itemType === 'bulk');

	const skuOptions = skuItemsToOptions(validSkuItems);

	const { item, control } = props;

	const handleAddNewInventoryTag = (tagLabel: string) => {
		const newTag: InventoryField = {
			id: newFirestoreId(),
			label: tagLabel,
			type: 'text',
			values: [],
		};
		props.addInventoryField(newTag);
		setNewSpecificationDialogOpen(false);
	};

	return (
		<>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Controller
						control={control}
						name="skuId"
						render={(renderProps) => (
							<TextField
								id="type"
								label="SKU"
								name="type"
								select
								value={renderProps.field.value}
								onChange={renderProps.field.onChange}
								margin="none"
								type="text"
								variant="outlined"
								fullWidth
							>
								<MenuItem value={NO_SKU_ID} disabled>
									{t('inventory.chooseSku', 'Choose SKU')}
								</MenuItem>
								{skuOptions.map((skuOption) => (
									<MenuItem value={skuOption.id} key={skuOption.id}>
										{skuOption.label}
									</MenuItem>
								))}
							</TextField>
						)}
					/>
				</Grid>
				{userLocations.length > 1 && (
					<Grid item xs={12}>
						<Controller
							control={control}
							name="locationId"
							render={(renderProps) => (
								<TextField
									id="type"
									label={t('inventory.chooseLocationLabel', 'Store')}
									name="type"
									select
									value={renderProps.field.value}
									onChange={renderProps.field.onChange}
									margin="none"
									type="text"
									variant="outlined"
									fullWidth
								>
									<MenuItem value="" disabled>
										{t('inventory.chooseLocation')}
									</MenuItem>
									{userLocations.map((location) => (
										<MenuItem value={location.id} key={location.id}>
											{location.name}
										</MenuItem>
									))}
								</TextField>
							)}
						/>
					</Grid>
				)}
				<Grid item xs={12}>
					<Controller
						control={control}
						name="identifiers"
						render={(renderProps) => (
							<InventoryIdentifierInput
								shopId={shopId}
								ids={renderProps.field.value}
								onIdsChange={renderProps.field.onChange}
								currentItemIds={item.identifiers}
								existingInventoryIds={existingInventoryIds}
								minOneIdRequired={isSingleItem(props.item)}
								showInvalidIdsHelper
							/>
						)}
					/>
				</Grid>
			</Grid>
			<Box mt={4} mb={2}>
				<Typography variant="body1">{t('inventory.specifications', 'Specifications')}</Typography>
			</Box>
			<Controller
				control={control}
				name="fieldValues"
				render={(renderProps) => (
					<SpecificationsList
						fields={props.inventoryFields}
						fieldValues={renderProps.field.value}
						setFieldValues={renderProps.field.onChange}
						fieldColSizes={{
							xs: 12,
						}}
					/>
				)}
			/>
			<Box mt={2}>
				<Button variant="outlined" onClick={() => setNewSpecificationDialogOpen(true)}>
					+ {t('inventory.addArticles.specifications.addNew')}
				</Button>
			</Box>
			{!!visibleFixedValueOptions.length && (
				<Box mt={2}>
					<Divider sx={{ mb: 2 }} />
					<Typography variant="body2" sx={{ mb: 2 }}>
						{t('inventory.specialFields', 'Special fields')}
					</Typography>
					<Controller
						control={control}
						name="fixedFieldValues"
						render={(renderProps) => (
							<FixedFieldValuesList
								fixedFieldValueOptions={visibleFixedValueOptions}
								fixedFieldValues={renderProps.field.value}
								setFixedFieldValues={renderProps.field.onChange}
								fieldColSizes={{
									xs: 12,
								}}
							/>
						)}
					/>
					<Divider sx={{ mb: 2 }} />
				</Box>
			)}
			{isNewSpecificationDialogOpen && (
				<NewSpecificationDialog
					isOpen={isNewSpecificationDialogOpen}
					onClose={() => setNewSpecificationDialogOpen(false)}
					handleAdd={handleAddNewInventoryTag}
					existingFieldLabels={props.inventoryFields.map((f) => f.label)}
				/>
			)}
		</>
	);
};

export default BasicInfoSection;
